import React, { FC } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';

interface ConfirmRulesDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isSaving: boolean;
  totalItemsMatched: number;
  ruleMatches: number[];
}

const ConfirmRulesDialog: FC<ConfirmRulesDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  isSaving,
  totalItemsMatched,
  ruleMatches,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
        Review &amp; Apply Rules
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="h3" component="p" fontWeight={700} sx={{ mt: 1 }}>
              {totalItemsMatched}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              catalog records will be affected by this ruleset.
            </Typography>

            <Typography
              variant="overline"
              fontWeight={700}
              sx={{ display: 'block', mb: 1 }}
            >
              Per-Rule Matches
            </Typography>
            {ruleMatches.map((count, index) => (
              <Typography key={index} variant="body1" sx={{ mb: 0.5 }}>
                Rule {index + 1}: {count} records
              </Typography>
            ))}

            <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
              Do you want to save and apply these rule changes?
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isLoading || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{
            bgcolor: '#c1440e',
            '&:hover': { bgcolor: '#a53b0b' },
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          {isSaving ? 'Saving...' : 'Confirm & Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmRulesDialog;
